import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getComments, createComment, deleteComment } from "../../services/comment.service.js";
import { formatDate } from "../../utils/date.js";
import "../../styles/visualization/visualizationComment.css";
import LoadTrigger from "../common/LoadTrigger.jsx";
import { useLocation, useNavigate } from "react-router-dom";

const VisualizationComment = ({
  publicationId,
  setCommentsCount,
  isLoggedIn,
  commentRef,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = useSelector((state) => state.user.userId);

  const navigateToLogin = () => {
    navigate("/login", { state: { from: location } });
  };

  const [comments, setComments] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(1);

  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  /* ---------- fetch comments ---------- */
  const fetchComments = async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const query = {
        page: pageRef.current,
        sortBy: "createdAt",
        sortOrder: "desc",
      };
      ++pageRef.current;

      const data = await getComments(publicationId, query);

      setComments((prev) => [...prev, ...data.records]);
      setHasMore(data.page < data.totalPages);
      setCommentsCount(data.totalCount);
    } catch (err) {
      --pageRef.current;
    } finally {
      setLoading(false);
    }
  };

  /* ---------- delete comment ---------- */
  const handleDelete = async (commentId) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await deleteComment(publicationId, commentId);
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
      setCommentsCount((n) => Math.max(0, n - 1));
    } catch (err) {}
  };

  /* ---------- create comment ---------- */
  const handleSubmit = async () => {
    if (!text.trim() || posting) return;

    setPosting(true);
    try {
      await createComment(publicationId, text.trim());

      setText("");
      pageRef.current = 1;
      setComments([]);
      setHasMore(true);
      fetchComments(); // reload fresh list
    } catch (err) {
    } finally {
      setPosting(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="viz-comment-login-hint">
        <button
          type="button"
          ref={commentRef}
          className="viz-comment-login-button"
          onClick={navigateToLogin}
        >
          Login to view and post comments
        </button>
      </div>
    );
  }

  return (
    <section ref={commentRef} className="viz-comments">
      <h3 className="viz-comments-title">Comments</h3>

      {/* Add comment */}
      <div className="viz-comment-input">
        <textarea
          placeholder="Write a comment..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
        />
        <button onClick={handleSubmit} disabled={posting}>
          {posting ? "Posting..." : "Post"}
        </button>
      </div>

      {/* Comment list */}
      <div className="viz-comment-list">
        {comments.map((c) => (
          <div key={c.commentId} className="viz-comment">
            <div className="viz-comment-header">
              <span
                className="viz-comment-user"
                onClick={() => navigate(`/profile/${c.user.userId}`)}
              >
                {c.user.firstName} {c.user.lastName}
              </span>

              <div className="viz-comment-header-right">
                <span className="viz-comment-date">
                  {formatDate(c.createdAt)}
                </span>
                {c.user.userId === currentUserId && (
                  <button
                    className="viz-comment-delete"
                    onClick={() => handleDelete(c.commentId)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            <p className="viz-comment-text">{c.text}</p>
          </div>
        ))}
      </div>

      <LoadTrigger
        isLoading={loading}
        onLoadMore={fetchComments}
        hasMore={hasMore}
      />
    </section>
  );
};

export default VisualizationComment;
